/**
 * The readable guard (combat design docs/design/combat/2026-10-11-guard-read, approved by the user 10-11): per weapon
 * family a guard whose silhouette is closed and vertical (ready is an open arrow toward the foe), a weak guard that sags
 * (guard points low), a jar for a blow taken on it, the guarded shuffle, and the shared break (`grImpact`: guard thrown
 * open, weapon knocked up and back) and open stance (`grOpen`: no guard for a while, arms wide, chest bare).
 * - Blade (jian, 立剑): the blade upright before the face, the off palm on its flat.
 * - Sabre (dao, 架刀): the blade laid across before the brow, the off palm on its back.
 * - Staff (立棍): the staff upright before the body, both hands on it (ready holds it level: the two never match).
 * - Fist (bare hands and the hidden arts, 双臂护面): both forearms up before the face.
 * Hand targets (classic design coordinates) are registered for the skeleton (rig/poseHands.ts); the renderer picks the
 * pose (render/guardRead.ts).
 */
import { registerPoseHands, type PoseHands } from '../rig/poseHands';
import { gait, type LimbSpec, type MotionDef, type PoseRigs, type Rig } from './kit';

export type GuardFamily = 'Blade' | 'Sabre' | 'Staff' | 'Fist';

/** The near (weapon) hand and the blade's angle on screen in profile (−π/2 straight up), per family: guard, weak. */
export const ARMS = {
  grBlade: { hand: [8, 25], ang: -1.72, front: true },
  grBladeWeak: { hand: [6, 29], ang: -2.3, front: true },
  grSabre: { hand: [9, 19], ang: -2.85, front: true },
  grSabreWeak: { hand: [7, 26], ang: -2.6, front: true },
  grStaff: { hand: [8, 26], ang: -1.7, front: true },
  grStaffWeak: { hand: [6, 30], ang: -2.25, front: true },
  grFist: { hand: [7, 20], ang: -1.4, front: true },
  grFistWeak: { hand: [7, 25], ang: -1.2, front: true },
  grImpact: { hand: [-12, 16], ang: -2.6, front: true },
  grOpen: { hand: [-11, 30], ang: 2.4, front: true },
} satisfies Record<string, LimbSpec>;

type Arm = keyof typeof ARMS;
const HANDS_OF: Record<GuardFamily, { guard: [Arm, [number, number]]; weak: [Arm, [number, number]] }> = {
  Blade: { guard: ['grBlade', [9, 20]], weak: ['grBladeWeak', [7, 26]] },
  Sabre: { guard: ['grSabre', [4, 19]], weak: ['grSabreWeak', [3, 25]] },
  Staff: { guard: ['grStaff', [9, 19]], weak: ['grStaffWeak', [7, 25]] },
  Fist: { guard: ['grFist', [8, 21]], weak: ['grFistWeak', [8, 27]] },
};

/** Sunk lower than ready, the chest square to the foe, the back a little rounded behind the guard. */
const BODY: Partial<Rig> = { o: 5, lean: 0, tilt: -1, f1: -8, f2: 5, far: 'fwd', hunch: 1, face: 'block' };
const BODY_OF: Record<GuardFamily, Partial<Rig>> = {
  Blade: BODY,
  Sabre: { ...BODY, tilt: 0 },
  Staff: { ...BODY, tilt: 0, o: 6, f1: -10, f2: 7 }, // a wider, lower base than ready (F3)
  Fist: { ...BODY, tilt: 4, lean: 1, hunch: 2 },
};

const guard = (f: GuardFamily): Partial<Rig> => ({ ...BODY_OF[f], arm: HANDS_OF[f].guard[0] });
/** Sinking and hunched, pushed back, the head down: the guard giving way. */
const weak = (f: GuardFamily): Partial<Rig> => ({
  ...BODY_OF[f],
  o: 9,
  tilt: f === 'Fist' ? -6 : -8,
  lean: -1,
  hunch: 4,
  headDx: -1,
  arm: HANDS_OF[f].weak[0],
});
/** A blow taken on the guard: rocked back on the heels, then settling into the guard again (`grJar*`). */
const jar = (f: GuardFamily): Partial<Rig> => ({ ...guard(f), o: 4, lean: -2, tilt: -7 });

/** The back and front foot's slide centres of the guarded shuffle (motions/guardWalk.ts, a little wider). */
const BACK = -6;
const FRONT = 4;
/** Key `i` of the guarded shuffle: the family's guard on short, planted steps (8 keys round the loop). */
function walkKey(f: GuardFamily, i: number): Partial<Rig> {
  const t = i / 8;
  const g = gait(t, 2, 0.75, 1.5, 1, 1);
  const shift = Math.sin(t * Math.PI * 2);
  return {
    ...guard(f),
    o: 4 + g.o,
    f1: Math.round(BACK + g.f1),
    f2: Math.round(FRONT + g.f2),
    l1: g.l1,
    l2: g.l2,
    sway: 0.4 * shift,
  };
}

const IMPACT: Partial<Rig> = { o: 3, lean: -3, tilt: -16, headDx: -2, f1: -10, f2: 6, far: 'back', face: 'block', arm: 'grImpact' };
const OPEN: Partial<Rig> = { o: 6, lean: -2, tilt: -12, headDx: -2, f1: -10, f2: 6, far: 'fwd', face: 'block', arm: 'grOpen' };

export type Poses =
  | `grGuard${GuardFamily}`
  | `grWeak${GuardFamily}`
  | `grJar${GuardFamily}`
  | `grW${GuardFamily}${0 | 1 | 2 | 3 | 4 | 5 | 6 | 7}`
  | 'grImpact'
  | 'grOpen';

export const POSES: PoseRigs<Poses> = {
  grGuardBlade: () => guard('Blade'),
  grWeakBlade: () => weak('Blade'),
  grJarBlade: () => jar('Blade'),
  grWBlade0: () => walkKey('Blade', 0),
  grWBlade1: () => walkKey('Blade', 1),
  grWBlade2: () => walkKey('Blade', 2),
  grWBlade3: () => walkKey('Blade', 3),
  grWBlade4: () => walkKey('Blade', 4),
  grWBlade5: () => walkKey('Blade', 5),
  grWBlade6: () => walkKey('Blade', 6),
  grWBlade7: () => walkKey('Blade', 7),
  grGuardSabre: () => guard('Sabre'),
  grWeakSabre: () => weak('Sabre'),
  grJarSabre: () => jar('Sabre'),
  grWSabre0: () => walkKey('Sabre', 0),
  grWSabre1: () => walkKey('Sabre', 1),
  grWSabre2: () => walkKey('Sabre', 2),
  grWSabre3: () => walkKey('Sabre', 3),
  grWSabre4: () => walkKey('Sabre', 4),
  grWSabre5: () => walkKey('Sabre', 5),
  grWSabre6: () => walkKey('Sabre', 6),
  grWSabre7: () => walkKey('Sabre', 7),
  grGuardStaff: () => guard('Staff'),
  grWeakStaff: () => weak('Staff'),
  grJarStaff: () => jar('Staff'),
  grWStaff0: () => walkKey('Staff', 0),
  grWStaff1: () => walkKey('Staff', 1),
  grWStaff2: () => walkKey('Staff', 2),
  grWStaff3: () => walkKey('Staff', 3),
  grWStaff4: () => walkKey('Staff', 4),
  grWStaff5: () => walkKey('Staff', 5),
  grWStaff6: () => walkKey('Staff', 6),
  grWStaff7: () => walkKey('Staff', 7),
  grGuardFist: () => guard('Fist'),
  grWeakFist: () => weak('Fist'),
  grJarFist: () => jar('Fist'),
  grWFist0: () => walkKey('Fist', 0),
  grWFist1: () => walkKey('Fist', 1),
  grWFist2: () => walkKey('Fist', 2),
  grWFist3: () => walkKey('Fist', 3),
  grWFist4: () => walkKey('Fist', 4),
  grWFist5: () => walkKey('Fist', 5),
  grWFist6: () => walkKey('Fist', 6),
  grWFist7: () => walkKey('Fist', 7),
  grImpact: () => IMPACT,
  grOpen: () => OPEN,
};

// The hands (both placed, so a pose reads the same on every figure).
const hands: Record<string, PoseHands> = {
  grImpact: { A: [-12, 16], B: [-11, 26] },
  grOpen: { A: [-11, 30], B: [11, 31] },
};
for (const f of ['Blade', 'Sabre', 'Staff', 'Fist'] as const) {
  const h = HANDS_OF[f];
  const g: PoseHands = { A: ARMS[h.guard[0]].hand as [number, number], B: h.guard[1] };
  hands[`grGuard${f}`] = g;
  hands[`grJar${f}`] = g;
  hands[`grWeak${f}`] = { A: ARMS[h.weak[0]].hand as [number, number], B: h.weak[1] };
  for (let i = 0; i < 8; i++) hands[`grW${f}${i}`] = g;
}
registerPoseHands(hands);

export const MOTIONS = [
  { id: 'grUpBlade', keys: ['ready', 'grGuardBlade'], ease: ['out'] },
  { id: 'grJarBlade', keys: ['grJarBlade', 'grGuardBlade'], ease: ['out'] },
  { id: 'grDropBlade', keys: ['grGuardBlade', 'ready'], ease: ['inOut'] },
  { id: 'grWalkBlade', keys: ['grWBlade0', 'grWBlade1', 'grWBlade2', 'grWBlade3', 'grWBlade4', 'grWBlade5', 'grWBlade6', 'grWBlade7'], loop: true },
  { id: 'grUpSabre', keys: ['ready', 'grGuardSabre'], ease: ['out'] },
  { id: 'grJarSabre', keys: ['grJarSabre', 'grGuardSabre'], ease: ['out'] },
  { id: 'grDropSabre', keys: ['grGuardSabre', 'ready'], ease: ['inOut'] },
  { id: 'grWalkSabre', keys: ['grWSabre0', 'grWSabre1', 'grWSabre2', 'grWSabre3', 'grWSabre4', 'grWSabre5', 'grWSabre6', 'grWSabre7'], loop: true },
  { id: 'grUpStaff', keys: ['ready', 'grGuardStaff'], ease: ['out'] },
  { id: 'grJarStaff', keys: ['grJarStaff', 'grGuardStaff'], ease: ['out'] },
  { id: 'grDropStaff', keys: ['grGuardStaff', 'ready'], ease: ['inOut'] },
  { id: 'grWalkStaff', keys: ['grWStaff0', 'grWStaff1', 'grWStaff2', 'grWStaff3', 'grWStaff4', 'grWStaff5', 'grWStaff6', 'grWStaff7'], loop: true },
  { id: 'grUpFist', keys: ['ready', 'grGuardFist'], ease: ['out'] },
  { id: 'grJarFist', keys: ['grJarFist', 'grGuardFist'], ease: ['out'] },
  { id: 'grDropFist', keys: ['grGuardFist', 'ready'], ease: ['inOut'] },
  { id: 'grWalkFist', keys: ['grWFist0', 'grWFist1', 'grWFist2', 'grWFist3', 'grWFist4', 'grWFist5', 'grWFist6', 'grWFist7'], loop: true },
] as const satisfies readonly MotionDef[];
